<template>
  <page-layout
    :title="groupTitle"
    :metaTitle="metaTitle"
    :back="backRoute"
    :loaded="groupLoaded"
  >
    <template #chip>
      <div v-if="contactsLoading" class="ml-3 mr-2">
        <spinner small />
      </div>
      <template v-if="!isMobile">
        <v-chip
          v-if="!contactsLoading"
          small
          :color="isIframe ? 'appMain' : 'secondary'"
          class="ml-2 mr-2"
          :class="isIframe ? 'light--text' : 'appMain--text'"
          >{{ contactsCount }}</v-chip
        >

        <smart-tooltip
          v-if="isTagGroup"
          type="tag-groups"
          category="groups"
          link
          icon="local_offer"
        />
      </template>
    </template>

    <template #header>
      <template v-if="showGroupControls">
        <group-controls
          :group="group"
          :disabled="isDirty"
          iconColor="light"
          @showModal="prepareModal($event, group)"
          @groupRemoved="exitGroup(null, true)"
        />

        <search-toggle-field
          v-model="search"
          class="mr-4"
          placeholder="Name, email, mobile, ID"
          :delay="0"
        />

        <v-btn
          v-if="isMobileOrTablet"
          color="secondary"
          class="no-min-width"
          @click="showFiltersDialog = true"
        >
          <icon-base icon="custom/settings" icon-color="appMain" />
        </v-btn>

        <v-btn
          v-if="!isMobile && hasPermissionToSendMessages"
          color="secondary"
          class="appMain--text"
          :disabled="isDirty"
          @click="prepareModal('ConversationsChat', group)"
        >
          Text group
        </v-btn>
      </template>

      <template v-if="!disableEditing && !isMobileOrTablet">
        <v-btn
          color="primary"
          @click="saveGroup"
          :disabled="(!isDirty && isGroupEdit) || groupLoading"
        >
          Save
        </v-btn>
      </template>
    </template>

    <v-layout class="form" align-stretch v-if="group">
      <v-form ref="form" class="groups-form">
        <template v-if="isMobileOrTablet">
          <!-- Mobile: Only contacts visible, filters in dialog -->
          <group-contacts
            :group="group"
            :isModal="isModal"
            :teamFilterOff="teamFilterOff"
            :additionalFilters="additionalFilters"
          />
        </template>

        <template v-else>
          <!-- Desktop: Side-by-side layout -->
          <group-form-sidebar
            ref="formSidebar"
            :group="group"
            :disableEditing="disableEditing"
            :disableNameEditing="disableNameEditing"
            :triggerOnly="triggerOnly"
            :teamFilterOff="teamFilterOff"
            :meta="meta"
          />
          <group-contacts
            :group="group"
            :isModal="isModal"
            :teamFilterOff="teamFilterOff"
            :additionalFilters="additionalFilters"
          />
        </template>
      </v-form>
    </v-layout>

    <!-- Mobile filters dialog -->
    <v-dialog
      v-if="isMobileOrTablet"
      v-model="showFiltersDialog"
      content-class="fixed-height-lg"
      max-width="680"
      eager
    >
      <modal-card
        title="Group Filters"
        :full="disableEditing"
        fixed
        @close="showFiltersDialog = false"
      >
        <group-form-sidebar
          ref="formSidebar"
          v-if="group"
          :group="group"
          :triggerOnly="triggerOnly"
          :teamFilterOff="teamFilterOff"
          :disableEditing="disableEditing"
          :disableNameEditing="disableNameEditing"
          :meta="meta"
        />

        <template v-slot:actions v-if="!disableEditing">
          <v-btn
            color="primary"
            @click="saveGroup"
            :disabled="(!isDirty && isGroupEdit) || groupLoading"
          >
            Save
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>

    <group-actions-modal v-model="actionModal" />
  </page-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { inIframe } from '@/globals';

import GroupControls from '../components/GroupControls';
import GroupFormSidebar from './GroupFormSidebar';
import GroupContacts from './GroupContacts';
import GroupActionsModal from '../dialogs/GroupDialogs';
import SmartTooltip from '@/components/basic/smart-tooltip/SmartTooltip.vue';
import SearchToggleField from '@/components/basic/search-toggle-field/SearchToggleField';

export default {
  name: 'GroupForm',
  components: {
    SmartTooltip,
    GroupControls,
    GroupFormSidebar,
    GroupContacts,
    GroupActionsModal,
    SearchToggleField,
  },
  props: {
    value: Boolean,
    id: String,
    isModal: Boolean,
    groupFor: String,
    groupName: String,
    triggerGroup: {},
    triggerOnly: Boolean,
    outerFilter: {
      type: Object,
      default: () => ({}),
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    teamFilterOffProp: Boolean,
  },
  data() {
    return {
      group: null,
      defaultFields: {
        default_for: [],
        filters: null,
        name: '',
        share_to: [],
        users: [],
      },
      actionModal: {
        type: null,
        group: null,
      },
      leaveConfirmed: false,
      search: '',
      showFiltersDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      'groups',
      'singleGroup',
      'groupLoading',
      'contactsLoading',
      'contactsInfo',
      'singleGroupContactsInfo',
    ]),
    ...mapGetters('fields', {
      fieldsLoaded: 'loaded',
    }),
    ...mapGetters('users', ['currentUser', 'isAdmin']),
    isMobileOrTablet() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    hasPermissionToSendMessages() {
      return (
        !this.isVectorRecruiting &&
        this.isFeatureEnabled('conversations') &&
        (this.isAdmin || this.hasPermission('group_messages'))
      );
    },
    isTagGroup() {
      return this.group?.type === 'tag';
    },
    showGroupControls() {
      return (
        this.isGroupEdit && this.groupFor !== 'trigger' && !this.triggerOnly
      );
    },
    groupLoaded() {
      return this.fieldsLoaded && !this.groupLoading;
    },
    isIframe() {
      return inIframe && this.$route.name !== 'portal-lesson';
    },
    groupTitle() {
      if (this.isMobile) return 'Group';
      return this.isGroupEdit ? 'Edit Group' : 'Create Group';
    },
    metaTitle() {
      if (!this.isGroupEdit) return this.groupTitle;
      return `${this.group?.name || this.groupTitle} - Groups`;
    },
    groupData() {
      if (this.triggerGroup) {
        return this.triggerGroup;
      }
      return this.id ? this.singleGroup(this.id) : this.defaultFields;
    },
    isDirty() {
      return !isEqual(this.group, this.groupData);
    },
    isGroupEdit() {
      return !!this.id || !!this.triggerGroup;
    },
    contactsCount() {
      let group = this.singleGroupContactsInfo(this.id);
      let count = 0;
      if (this.contactsInfo) {
        count = this.contactsInfo.count;
      } else if (group?.count) {
        count = group?.count;
      }

      return (count || 0).toLocaleString('en-US');
    },
    disableEditing() {
      if (!this.currentUser || !this.groupData) return;
      const { id, user_id } = this.groupData;
      return (
        this.currentUser.role === 'guest' &&
        id &&
        user_id != this.currentUser.id
      );
    },
    disableNameEditing() {
      return !!this.groupName && this.triggerOnly;
    },
    backRoute() {
      const { name, fullPath } = this.previousRoute || {};
      const exitRoutes = ['contact-edit', 'group-create', 'group-edit'];
      if (exitRoutes.includes(name) && this.id) {
        return {
          name: this.group?.folder_id ? 'groups-folder' : 'groups',
          params: {
            folderId: this.group?.folder_id,
          },
        };
      }

      return fullPath || '/groups';
    },
    teamFilterOff() {
      return (
        this.teamFilterOffProp || this.$route?.name === 'settings-team-edit'
      );
    },
    additionalFilters() {
      return this.search ? { name: this.search } : {};
    },
  },
  watch: {
    groupData: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal) {
          return false;
        }

        if (!this.group || oldVal) {
          this.setGroup();
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'loadSingleGroup',
      'loadFilteredContacts',
      'createGroup',
      'updateGroup',
    ]),
    ...mapActions('fields', { loadFields: 'load' }),
    ...mapActions('groupsFolders', { loadGroupsFolders: 'load' }),

    validate() {
      const formSidebar = this.$refs.formSidebar;
      if (formSidebar) {
        formSidebar.prepareFiltersToSend();
      }
      return this.$refs.form.validate();
    },

    saveGroup() {
      if (!this.validate()) {
        this.$refs.formSidebar.openPanelsWithErrors();
        return;
      }

      // Group for campaign triggers
      if (this.triggerOnly) {
        this.$emit('groupSaved', {
          ...this.group,
          count: this.contactsCount,
        });
        this.$emit('input', false);
        return;
      }

      if (this.isGroupEdit) {
        this.updateGroupConfirmation(() => {
          this.saveData('updateGroup');
        });
        return;
      }

      this.saveData('createGroup');
    },

    updateGroupConfirmation(onConfirm) {
      this.openConfirmationModal({
        title: 'Update Group',
        buttonText: 'Yes, Update Group',
        cancelButtonText: 'No',
        text:
          '<p class="mb-1">Are you sure you want to update this group?</p>' +
          `<p class="mb-0">This group now matches <strong>${this.contactsCount}</strong> contacts.</p>`,
        onConfirm,
      });
    },

    leaveGroupConfirmation(path) {
      this.openConfirmationModal({
        title: 'Unsaved Changes',
        buttonText: 'Save',
        cancelButtonText: 'Cancel',
        text: 'You have made unsaved changes to this group.<br> If you would like to exit please save or discard changes.',
        onDiscard: () => {
          this.confirmLeave(path, false);
        },
        onConfirm: async () => {
          await this.$nextTick();
          this.confirmLeave(path, true);
        },
      });
    },

    saveData(method) {
      const data = {
        ...this.group,
        count: this.contactsCount,
        for: this.groupFor,
      };

      this[method](data).then((group) => {
        this.leaveConfirmed = false;
        this.exitGroup(group, false);
      });
    },

    exitGroup(group, removed = false) {
      if (this.isModal) {
        if (group) {
          this.$emit('groupSaved', group);
        }
        return this.$emit('input', false);
      }

      if (!this.isGroupEdit || removed) {
        this.leaveConfirmed = true;
        let path = { name: 'groups' };
        if (group && group.id) {
          path = { name: 'group-edit', params: { id: group.id } };
        }
        this.$router.push(path);
      }
    },

    checkParentFolder() {
      const parentFolderId = this.previousRoute?.params?.folderId;
      if (parentFolderId && !this.group?.id) {
        this.defaultFields.folder_id = parentFolderId;
      }
    },

    setGroup() {
      if (this.outerFilter) {
        this.defaultFields.filters = this.outerFilter;
      }

      this.checkParentFolder();

      this.group = cloneDeep(this.groupData || this.defaultFields);
      if (this.groupName) {
        this.group.name = this.groupName;
      }
    },

    prepareModal(type, group) {
      this.actionModal = { type, group };
    },

    confirmLeave(path, save) {
      this.leaveConfirmed = true;
      if (save) {
        return this.saveGroup();
      }
      this.$router.push(path);
    },

    async checkResources() {
      await this.loadFields();
      this.loadSingleGroup(this.id);
      this.loadGroupsFolders();
    },
  },
  mounted() {
    this.checkResources();
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.leaveConfirmed) {
      this.leaveGroupConfirmation(to.path);
    } else {
      next();
    }
  },
};
</script>

<style lang="less" src="@/assets/styles/basic/form.less"></style>

<style lang="less">
.groups-form.v-form {
  @media only screen and (max-width: 959px) {
    display: block;

    & > div,
    .form__sidebar {
      max-width: 100%;
      width: 100%;
    }
  }

  .v-window {
    border-top: 1px solid #eee;
  }
}
</style>
